import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase/config';

interface PropCloudAILogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const PropCloudAILogo: React.FC<PropCloudAILogoProps> = ({ 
  className = '', 
  size = 'md' 
}) => {
  const [logoUrl, setLogoUrl] = useState<string>('');
  
  useEffect(() => {
    // First check localStorage for immediate display
    const cachedLogo = localStorage.getItem('systemLogoUrl');
    if (cachedLogo) {
      setLogoUrl(cachedLogo);
    }
    
    // Then fetch from Firestore
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'branding'));
      if (settingsDoc.exists() && settingsDoc.data()?.logoUrl) {
        const url = settingsDoc.data().logoUrl;
        setLogoUrl(url);
        localStorage.setItem('systemLogoUrl', url);
      }
    } catch (error) {
      console.error('Failed to load logo:', error);
    }
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  // If we have an uploaded logo, use it
  if (logoUrl) {
    return (
      <img 
        src={logoUrl} 
        alt="PropCloudAI" 
        className={`${sizeClasses[size]} ${className} object-contain`}
      />
    );
  }

  // Otherwise use the default SVG - Cloud with AI Brain Network design
  return (
    <svg 
      className={`${sizeClasses[size]} ${className}`}
      viewBox="0 0 100 100" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        {/* Cloud shape outline */}
        <path 
          d="M25 60 C15 60 10 55 10 45 C10 35 15 30 25 30 C25 25 30 20 40 20 C50 20 55 25 55 30 C60 25 70 25 75 30 C80 35 80 40 75 45 C85 45 90 50 90 60 C90 70 85 75 75 75 L25 75 C15 75 10 70 10 60 Z"
          fill="#f3f9ed"
          stroke="#6eb041"
          strokeWidth="2"
        />
        
        {/* AI Brain Network inside cloud */}
        {/* Network nodes */}
        <circle cx="35" cy="40" r="4" fill="#6eb041"/>
        <circle cx="50" cy="35" r="5" fill="#6eb041"/>
        <circle cx="65" cy="45" r="4" fill="#85c255"/>
        <circle cx="40" cy="55" r="4.5" fill="#85c255"/>
        <circle cx="55" cy="50" r="5" fill="#6eb041"/>
        <circle cx="60" cy="60" r="3.5" fill="#85c255"/>
        
        {/* Connecting lines forming brain network */}
        <path d="M35 40 L50 35 L65 45" stroke="#6eb041" strokeWidth="2" opacity="0.7"/>
        <path d="M35 40 L40 55 L55 50" stroke="#85c255" strokeWidth="2" opacity="0.6"/>
        <path d="M50 35 L55 50 L60 60" stroke="#6eb041" strokeWidth="1.5" opacity="0.5"/>
        <path d="M65 45 L55 50" stroke="#85c255" strokeWidth="1.5" opacity="0.5"/>
        <path d="M40 55 L60 60" stroke="#6eb041" strokeWidth="1.5" opacity="0.4"/>
        
        {/* Central AI core */}
        <circle cx="50" cy="47" r="7" fill="#6eb041" opacity="0.3"/>
        <circle cx="50" cy="47" r="3" fill="#6eb041"/>
      </g>
    </svg>
  );
};